<template>
  <div class="code-card" :class="{ 'is-expanded': isExpanded }">
    <!-- 卡片头部 -->
    <div class="card-header">
      <h3 class="card-title">{{ title }}</h3>
      <p v-if="description" class="card-description">{{ description }}</p>
    </div>

    <!-- 功能展示区域 -->
    <div class="card-demo">
      <slot></slot>
    </div>

    <!-- 展开按钮 -->
    <div class="card-footer">
      <button class="expand-btn" @click="toggleExpand">
        <i class="el-icon-arrow-down" :class="{ 'is-rotated': isExpanded }"></i>
        <span>{{ isExpanded ? '收起代码' : '展开代码' }}</span>
      </button>
    </div>

    <!-- 代码展示区域 -->
    <transition name="slide">
      <div v-show="isExpanded" class="code-section">
        <div class="code-header">
          <span class="code-label">{{ codeLabel }}</span>
          <button class="copy-btn" @click="copyCode">
            <i class="el-icon-document-copy"></i>
            <span>{{ copyText }}</span>
          </button>
        </div>
        <pre class="code-content"><code>{{ codeContent }}</code></pre>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'CodeCard',
  props: {
    // 标题
    title: {
      type: String,
      required: true
    },
    // 描述
    description: {
      type: String,
      default: ''
    },
    // 代码内容
    codeContent: {
      type: String,
      default: ''
    },
    // 代码标签
    codeLabel: {
      type: String,
      default: 'Vue'
    },
    // 默认是否展开
    defaultExpanded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isExpanded: this.defaultExpanded,
      copyText: '复制'
    }
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded
      this.$emit('toggle', this.isExpanded)
    },
    async copyCode() {
      const text = this.codeContent
      let success = false

      // 方法1: 使用现代 Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(text)
          success = true
        } catch (err) {
          console.warn('Clipboard API 失败，尝试其他方法')
        }
      }

      // 方法2: 使用 Selection API + execCommand
      if (!success) {
        try {
          success = this.copyWithSelection(text)
        } catch (err) {
          console.warn('Selection API 失败，尝试其他方法')
        }
      }

      // 方法3: 使用 textarea 辅助复制
      if (!success) {
        try {
          success = this.copyWithTextarea(text)
        } catch (err) {
          console.warn('Textarea 方法失败')
        }
      }

      // 显示结果
      if (success) {
        this.copyText = '已复制'
        this.$toast.success('代码已复制到剪贴板')
        setTimeout(() => {
          this.copyText = '复制'
        }, 2000)
      } else {
        this.$toast.error('复制失败，请手动复制')
      }
    },

    // 使用 Selection API 复制
    copyWithSelection(text) {
      const selection = window.getSelection()
      const range = document.createRange()
      
      // 创建临时元素
      const tempElement = document.createElement('div')
      tempElement.style.position = 'absolute'
      tempElement.style.left = '-9999px'
      tempElement.style.whiteSpace = 'pre-wrap'
      tempElement.textContent = text
      document.body.appendChild(tempElement)
      
      try {
        range.selectNodeContents(tempElement)
        selection.removeAllRanges()
        selection.addRange(range)
        const success = document.execCommand('copy')
        selection.removeAllRanges()
        return success
      } finally {
        document.body.removeChild(tempElement)
      }
    },

    // 使用 Textarea 辅助复制
    copyWithTextarea(text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      textarea.style.top = '0'
      textarea.style.opacity = '0'
      textarea.style.pointerEvents = 'none'
      document.body.appendChild(textarea)
      
      try {
        // iOS 兼容性处理
        const isIOS = navigator.userAgent.match(/ipad|ipod|iphone/i)
        if (isIOS) {
          const range = document.createRange()
          range.selectNodeContents(textarea)
          const selection = window.getSelection()
          selection.removeAllRanges()
          selection.addRange(range)
          textarea.setSelectionRange(0, 999999)
        } else {
          textarea.select()
        }
        return document.execCommand('copy')
      } finally {
        document.body.removeChild(textarea)
      }
    }
  }
}
</script>

<style scoped>
.code-card {
  background: var(--surface-1);
  border-radius: 8px;
  box-shadow: var(--shadow-surface-1);
  transition: all 0.3s ease;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.code-card:hover {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.12);
}

.code-card.is-expanded {
  box-shadow: 0 6px 24px 0 rgba(0, 0, 0, 0.15);
}

.card-header {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.card-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  line-height: 1.4;
}

.card-description {
  margin: 8px 0 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
}

.card-demo {
  padding: 20px;
  min-height: 120px;
  background: var(--surface-2);
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-footer {
  padding: 12px 20px;
  text-align: center;
  border-top: 1px solid rgba(31, 45, 61, 0.06);
}

.expand-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  outline: none;
}

.expand-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.expand-btn:active {
  transform: translateY(0);
}

.expand-btn i {
  transition: transform 0.3s ease;
  font-weight: bold;
}

.expand-btn i.is-rotated {
  transform: rotate(180deg);
}

.code-section {
  background: #1E1E1E;
  border-top: 3px solid #667eea;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #2D2D2D;
  border-bottom: 1px solid #3A3A3A;
}

.code-label {
  display: inline-block;
  padding: 4px 12px;
  background: #667eea;
  color: #ffffff;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: transparent;
  color: #909399;
  border: 1px solid #4A4A4A;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
  outline: none;
}

.copy-btn:hover {
  color: #ffffff;
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.1);
}

.code-content {
  margin: 0;
  padding: 20px;
  color: #D4D4D4;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.code-content code {
  color: inherit;
  font-family: inherit;
}

/* 展开动画 */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

/* 滚动条美化 */
.code-content::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.code-content::-webkit-scrollbar-track {
  background: #2D2D2D;
}

.code-content::-webkit-scrollbar-thumb {
  background: #4A4A4A;
  border-radius: 4px;
}

.code-content::-webkit-scrollbar-thumb:hover {
  background: #5A5A5A;
}
</style>
